import { useEffect } from "react";

export const SITE_URL = "https://fdruryandsons.co.uk";
export const SITE_NAME = "F. Drury & Sons";
export const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=1600&q=80";

const MANAGED_MARKER = "data-seo-managed";

function upsertMeta({ name, property, content }) {
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(MANAGED_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink({ rel, href, hreflang }) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    el.setAttribute(MANAGED_MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function removeManagedJsonLd() {
  document
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_MARKER}]`)
    .forEach((n) => n.remove());
}

function injectJsonLd(obj, idx) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(MANAGED_MARKER, "true");
  script.setAttribute("data-seo-idx", String(idx));
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
}

/**
 * Per-route SEO manager. Updates title, meta description/robots/keywords,
 * canonical, hreflang, OG, Twitter, and injects JSON-LD structured data.
 * Cleans up its own injected JSON-LD when the route unmounts.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  imageAlt = "F. Drury & Sons — Wiltshire Abattoir & Wholesale Butcher",
  type = "website",
  schema = [],
  breadcrumbs = [],
  noindex = false,
  keywords,
}) {
  useEffect(() => {
    const url = `${SITE_URL}${path === "/" ? "/" : path}`;
    const fullTitle = title?.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`;

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", "en-GB");

    upsertMeta({ name: "description", content: description });
    if (keywords) upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({
      name: "robots",
      content: noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1",
    });

    upsertLink({ rel: "canonical", href: url });
    upsertLink({ rel: "alternate", href: url, hreflang: "en-gb" });
    upsertLink({ rel: "alternate", href: url, hreflang: "x-default" });

    // Open Graph
    upsertMeta({ property: "og:type", content: type });
    upsertMeta({ property: "og:locale", content: "en_GB" });
    upsertMeta({ property: "og:site_name", content: SITE_NAME });
    upsertMeta({ property: "og:title", content: fullTitle });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:image", content: image });
    upsertMeta({ property: "og:image:secure_url", content: image });
    upsertMeta({ property: "og:image:alt", content: imageAlt });

    // Twitter
    upsertMeta({ name: "twitter:card", content: "summary_large_image" });
    upsertMeta({ name: "twitter:title", content: fullTitle });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: image });
    upsertMeta({ name: "twitter:image:alt", content: imageAlt });

    // Structured data
    removeManagedJsonLd();
    const ld = [];
    if (breadcrumbs && breadcrumbs.length > 0) {
      ld.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path === "/" ? "/" : b.path}`,
        })),
      });
    }
    if (Array.isArray(schema)) ld.push(...schema);
    ld.forEach((obj, i) => injectJsonLd(obj, i));

    return () => {
      removeManagedJsonLd();
    };
  }, [
    title,
    description,
    path,
    image,
    imageAlt,
    type,
    keywords,
    noindex,
    JSON.stringify(schema),
    JSON.stringify(breadcrumbs),
  ]);

  return null;
}
